import JobSkillWorkersPage from '@/pages/jobs/skill-workers'
import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { buildServiceVariantHomePath, getServiceInstanceIdFromSearch, getServiceInstanceIdFromToken, ORG_ORGANIZATION_PAGE_PATH, withServiceInstanceId } from '@/lib/service-instance'
import { evaluateRouteAccess } from '@/lib/route-access'
import { isBranchOperatorWorkspaceContextFromPost } from '@/lib/org-hierarchy-from-instance'

// Auth pages
import LoginPage from '@/pages/auth/login'
import SignupPage from '@/pages/auth/signup'
import SignupCompanyPage from '@/pages/auth/signup-company'
import SignupServicesCompanyPage from '@/pages/auth/signup-services-company'
import SignupStartCompanyPage from '@/pages/auth/signup-start-company'
import TrialExpiredPage from '@/pages/billing/trial-expired'
import AuthHandoffPage from '@/pages/auth/auth-handoff'
import SignupVendorLookupPage from '@/pages/auth/signup-vendor-lookup'
import SignupVendorPage from '@/pages/auth/signup-vendor'
import SignupPublicPage from '@/pages/auth/signup-public'
import VerifyEmailPage from '@/pages/auth/verify-email'
import ForgotPasswordPage from '@/pages/auth/forgot-password'
import ResetPasswordPage from '@/pages/auth/reset-password'
import { AuthThemeProvider } from '@/lib/auth-theme'
import PostsSelectionPage from '@/pages/posts/index'

// Protected layout
import ProtectedLayout from '@/components/layout/protected-layout'

// Dashboard
import DashboardPage from '@/pages/dashboard/index'
import SalesDashboardPage from '@/pages/sales/dashboard'
import EnquiryFollowupsPage from '@/pages/sales/enquiry-followups'
import SalesFollowUpsPage from '@/pages/sales/follow-ups'

// Jobs
import JobCustomersPage from '@/pages/jobs/customers'
import CustomerDetailPage from '@/pages/customers/customer-detail-page'
import JobCreatePage from '@/pages/jobs/create'
import JobConfigurationPage from '@/pages/jobs/configuration'
import JobChecklistsPage from '@/pages/jobs/checklists'
import JobLifecyclePage from '@/pages/jobs/lifecycle'
import ProjectsListPage from '@/pages/projects/list.tsx'
import ProjectCreatePage from '@/pages/projects/create.tsx'
import EventsListPage from '@/pages/events/list.tsx'
import EventsDashboardPage from '@/pages/events/dashboard.tsx'
import EventDetailPage from '@/pages/events/detail.tsx'
import EventCreatePage from '@/pages/events/create.tsx'
import EventDaysPage from '@/pages/events/workdays.tsx'
import EventAssignmentsPage from '@/pages/events/assignments.tsx'
import EventAssignmentCreatePage from '@/pages/events/assignments-create.tsx'
import EventAssignmentEditPage from '@/pages/events/assignments-edit.tsx'
import EventAssignmentDetailsPage from '@/pages/events/assignments-details.tsx'
import EventConfigurationPage from '@/pages/events/configuration.tsx'
import EventWorkflowsPage from '@/pages/events/workflows.tsx'
import EventCustomersPage from '@/pages/events/customers.tsx'
import EventLifecyclePage from '@/pages/events/lifecycle.tsx'
import EventChecklistsPage from '@/pages/events/checklists.tsx'
import EventOperationsPage from '@/pages/events/operations.tsx'
import ProjectAssignmentsPage from '@/pages/projects/assignments.tsx'
import ProjectWorkdaysPage from '@/pages/projects/workdays.tsx'
import CategoriesPage from '@/pages/categories/index'
import TagsPage from '@/pages/tags/index'

// Workers
import WorkerApprovalsPage from '@/pages/workers/approvals'
import WorkerHotlineHistoryPage from '@/pages/workers/hotline-history'
import WorkerCreatePage from '@/pages/workers/create'
import WorkerEditPage from '@/pages/workers/edit'
import UserDetailPage from '@/pages/users/detail'
import LocationsPage from '@/pages/locations/index'
import LivePage from '@/pages/live/index'
import RealtimeOperationsPage from '@/pages/live/realtime'
import ScheduleWorkersPage from '@/pages/live/schedule-workers'
import LiveAttendancePage from '@/pages/attendance/live'
import AttendanceBoardPage from '@/pages/attendance/index'
import TimeLogsPage from '@/pages/attendance/logs'
import ClockStationPage from '@/pages/attendance/clock'
import AttendanceApprovalsPage from '@/pages/attendance/approvals'

// Roles
import CatalogRolesPage from '@/pages/org/catalog-roles'

// Assignments
import AssignmentsPage from '@/pages/assignments/list'
import AssignmentDetailPage from '@/pages/assignments/detail'
import AssignmentJobViewPage from '@/pages/assignments/job-view'
import AssignmentFormPage from '@/pages/assignments/form'

// WorkDays
import WorkDaysPage from '@/pages/workdays/list'
import TimelinePage from '@/pages/timeline/index'

// Finance
import FinanceInvoicesPage from '@/pages/finance/invoices'
import JobInvoicesPage from '@/pages/finance/invoice-preview'
import JobInvoiceA4PreviewPage from '@/pages/finance/invoice-a4-preview'
import InvoicePaymentsPage from '@/pages/finance/invoice-payments'
import InvoicePaymentsListPage from '@/pages/finance/invoice-payments-list'
import PublicInvoicePaymentPage from '@/pages/finance/public-invoice-payment'
import WorkerInvoicesPage from '@/pages/finance/worker-invoices'
import WorkerDailyInvoicesPage from '@/pages/finance/worker-daily-invoices'
import WorkerDraftInvoicePage from '@/pages/finance/worker-draft-invoice'
import CompanyPaymentsPage from '@/pages/finance/company-payments'
import SettlementInvoicesPage from '@/pages/finance/settlement-invoices'

// Profile
import TreeNodeChildPage from '@/pages/tree/node-child'
import TreeNodeDetailsPage from '@/pages/tree/node-details'
import OrgServiceInstanceListRootRedirect from '@/pages/tree/service-instance-list-root-redirect'
import BranchWorkspacePage from '@/pages/org/branch-workspace'
import OrgTemplatesPage from '@/pages/org/templates-page'
import InAppsPage from '@/pages/org/in-apps'
import MetaCredentialsPage from '@/pages/org/meta-credentials'
import TreeClientStoresPage from '@/pages/tree/client-stores'
import TreeClientChildPage from '@/pages/tree/client-child'
import TreeClientDetailsPage from '@/pages/tree/client-details'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import RolesPage from '@/pages/roles/index'
import FeaturesPage from '@/pages/features/index'
import ServiceVariantDashboard from '@/pages/services/dashboard'
import CommerceTaxesPage from '@/pages/commerce/taxes'
import CommerceQuotationsPage from '@/pages/commerce/quotations'
import CommerceQuotationDetailPage from '@/pages/commerce/quotation-detail'
import PublicOrderPaymentPage from '@/pages/commerce/public-order-payment'
import OrderConfirmationPage from '@/pages/commerce/order-confirmation'
import WhatsAppMessagesPage from '@/pages/commerce/whatsapp-messages'
import WhatsAppCatalogPage from '@/pages/commerce/whatsapp-catalog'
import CommerceOrderDetailPage from '@/pages/commerce/order-detail'
import CommerceTagsetsPage from '@/pages/commerce/tagsets'
import CommerceTagsPage from '@/pages/commerce/tags'
import CommerceItemTagsPage from '@/pages/commerce/item-tags'
import WorkerSkillsPage from '@/pages/commerce/worker-skills'
import CrewsPage from '@/pages/commerce/crews'
import CrewsStaffPage from '@/pages/commerce/crews-staff'
import PayScalesPage from '@/pages/commerce/pay-scales'
const SalesEnquiriesPage = lazy(() => import('@/pages/sales/enquiries'))
import ProjectWorkflowsPage from '@/pages/projects/workflows.tsx'
const JobWorkflowsPage = lazy(() => import('@/pages/jobs/workflows'))
const JobComplaintsPage = lazy(() => import('@/pages/jobs/complaints'))
const JobsListPage = lazy(() => import('@/pages/jobs/list'))
const LiveJobsPage = lazy(() => import('@/pages/jobs/live'))
const JobDetailPage = lazy(() => import('@/pages/jobs/detail'))
const WorkersListPage = lazy(() => import('@/pages/workers/list'))
const WorkerDetailPage = lazy(() => import('@/pages/workers/detail'))
const UsersPage = lazy(() => import('@/pages/users/index'))
const VehiclesPage = lazy(() => import('@/pages/vehicles/index'))
const AssetsPage = lazy(() => import('@/pages/assets/index'))
const AssetDetailPage = lazy(() => import('@/pages/assets/detail'))
const AssetMaintenanceBoardPage = lazy(() => import('@/pages/assets/maintenance'))
const AssetConfigurationPage = lazy(() => import('@/pages/assets/configuration'))
const AssetReportsPage = lazy(() => import('@/pages/assets/reports'))
const AssetContractsPage = lazy(() => import('@/pages/assets/contracts'))
const AssetLookupPage = lazy(() => import('@/pages/assets/lookup'))
const MyAssetsPage = lazy(() => import('@/pages/assets/my-assets'))
const AssetLocationsPage = lazy(() => import('@/pages/assets/locations'))
const ManageRolesPage = lazy(() => import('@/pages/roles/list'))
const WorkDayEntryFormPage = lazy(() => import('@/pages/workdays/form'))
const ExpensesPage = lazy(() => import('@/pages/finance/expenses'))
const ParticularsPage = lazy(() => import('@/pages/finance/particulars'))
const WorkerPaymentsPage = lazy(() => import('@/pages/finance/worker-payments'))
const ProfilePage = lazy(() => import('@/pages/profile/index'))
const FeaturesSettingsPage = lazy(() => import('@/pages/settings/features'))
const HelpSupportPage = lazy(() => import('@/pages/help-support'))
const ReportsPage = lazy(() => import('@/pages/reports/index'))
const WorkerReportPage = lazy(() => import('@/pages/reports/worker'))
const CompanyReportPage = lazy(() => import('@/pages/reports/company'))
const ClientReportPage = lazy(() => import('@/pages/reports/client'))
const TenantsPage = lazy(() => import('@/pages/organisation/tenants'))
const TreePage = lazy(() => import('@/pages/tree/index'))
const TreeServiceInstancesPage = lazy(() => import('@/pages/tree/service-instances'))
const TreeServiceInstanceFormPage = lazy(() => import('@/pages/tree/service-instance-form'))
const TreeServiceInstanceMediaPage = lazy(() => import('@/pages/tree/service-instance-media'))
const OrganisationVerticalsPage = lazy(() => import('@/pages/org/verticals'))
const CommercePosPage = lazy(() => import('@/pages/commerce/pos'))
const CommerceProductsPage = lazy(() => import('@/pages/commerce/products'))
const CommerceProductVariantsPage = lazy(() => import('@/pages/commerce/product-variants'))
const CommerceCategoriesPage = lazy(() => import('@/pages/commerce/categories'))
const CommerceOrdersPage = lazy(() => import('@/pages/commerce/orders'))
const CommerceBookingsDashboardPage = lazy(() => import('@/pages/commerce/bookings-dashboard'))
const CommerceOrderPaymentsPage = lazy(() => import('@/pages/commerce/order-payments'))
const CommerceCustomerCreditPage = lazy(() => import('@/pages/commerce/customer-credit'))
const CommerceCustomerCreditDetailPage = lazy(() => import('@/pages/commerce/customer-credit-detail'))
const CommerceRefundsPage = lazy(() => import('@/pages/commerce/refunds'))
const CommerceReviewsPage = lazy(() => import('@/pages/commerce/reviews'))
const CommerceRefundPoliciesPage = lazy(() => import('@/pages/commerce/refund-policies'))
const CommerceQuotationFormPage = lazy(() => import('@/pages/commerce/quotation-form'))
const CommerceQuotationPosPage = lazy(() => import('@/pages/commerce/quotation-pos'))
const CommerceCustomersPage = lazy(() => import('@/pages/commerce/customers'))
const CommerceOffersPage = lazy(() => import('@/pages/commerce/offers'))
const MembershipVariantDashboard = lazy(() => import('@/pages/membership/dashboard'))
const AcademicDashboardPage = lazy(() => import('@/pages/academic/dashboard'))
const AcademicSectionPage = lazy(() => import('@/pages/academic/section'))
const MembershipsPage = lazy(() => import('@/pages/commerce/memberships'))
const MembershipSubscriptionPaymentsPage = lazy(() => import('@/pages/commerce/membership-subscription-payments'))
const MembershipSubscriptionMembersPage = lazy(() => import('@/pages/commerce/membership-subscription-members'))
const CompleteOrderPage = lazy(() => import('@/pages/commerce/complete-order'))
const PlannerPage = lazy(() => import('@/pages/planner/index'))
const FundraisingDashboardPage = lazy(() => import('@/pages/fundraising/dashboard'))
const FundraisingCampaignsPage = lazy(() => import('@/pages/fundraising/list'))
const FundraisingCampaignDetailPage = lazy(() => import('@/pages/fundraising/detail'))
const FundraisingCampaignFormPage = lazy(() => import('@/pages/fundraising/create'))
const FundraisingCategoriesPage = lazy(() => import('@/pages/fundraising/categories'))
const FundraisingDonorsPage = lazy(() => import('@/pages/fundraising/donors'))
const FundraisingDonationsPage = lazy(() => import('@/pages/fundraising/donations'))
const FundraisingSettingsPage = lazy(() => import('@/pages/fundraising/settings'))
const PublicDonationPage = lazy(() => import('@/pages/fundraising/public-donation'))
const PublicPrivacyPolicyPage = lazy(() => import('@/pages/org/public-privacy-policy'))
const PublicAccountDeletionPage = lazy(() => import('@/pages/org/public-account-deletion'))
const MarketingAnnouncementsPage = lazy(() => import('@/pages/marketing/announcements-list'))
const MarketingAnnouncementFormPage = lazy(() => import('@/pages/marketing/announcement-form'))
const VenueDashboardPage = lazy(() => import('@/pages/venue/dashboard'))
const VenueFiguresPage = lazy(() => import('@/pages/venue/figures'))
const VenueDeitiesPage = lazy(() => import('@/pages/venue/deities'))
const VenueEventsPage = lazy(() => import('@/pages/venue/events'))
const VenueFestivalsPage = lazy(() => import('@/pages/venue/festivals'))
const VenueVolunteersPage = lazy(() => import('@/pages/venue/volunteers'))
const VenueCalendarPage = lazy(() => import('@/pages/venue/calendar'))
const VenueNewsPage = lazy(() => import('@/pages/venue/news'))
const VenueHomeContentAdminPage = lazy(() => import('@/pages/venue/home-content-admin'))
const VenueAboutTempleAdminPage = lazy(() => import('@/pages/venue/about-temple-admin'))
const VenueActivityAdminPage = lazy(() => import('@/pages/venue/activity-admin'))
const VenueGalleryAdminPage = lazy(() => import('@/pages/venue/gallery-admin'))
const VenueVisionMissionAdminPage = lazy(() => import('@/pages/venue/vision-mission-admin'))
const VenueDynamicSectionRecordsAdminPage = lazy(() => import('@/pages/venue/dynamic-section-records-admin'))
const VenueSectionLabelsAdminPage = lazy(() => import('@/pages/venue/section-labels-admin'))
const VenuePoojaTimingsAdminPage = lazy(() => import('@/pages/venue/pooja-timings-admin'))
const VenueTopBannersEditPage = lazy(() => import('@/pages/venue/top-banners-edit'))
const VenueLanguagesAdminPage = lazy(() => import('@/pages/venue/languages-admin'))

function CrewTemplatesRedirect() {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('tab', 'templates')
  return <Navigate to={`/services/crews?${params.toString()}`} replace />
}

function PlannerTabRedirect() {
  const location = useLocation()
  return <Navigate to={`/planner/planner-model${location.search}${location.hash}`} replace />
}

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const hasHydrated = useAuthStore((state) => state.hasHydrated)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  if (!hasHydrated) {
    return null
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }
  
  return <>{children}</>
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const hasHydrated = useAuthStore((state) => state.hasHydrated)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const serviceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const resolvedServiceInstanceId = serviceInstanceId || getServiceInstanceIdFromToken(accessToken)
  const from = typeof location.state === 'object' && location.state && 'from' in location.state
    ? location.state.from
    : null

  if (!hasHydrated) {
    return null
  }
  
  if (isAuthenticated) {
    if (!currentPost && availablePosts.length > 0) {
      return <Navigate to={buildPostSelectionPath(resolvedServiceInstanceId, typeof from === 'string' ? from : null)} replace />
    }

    if (!currentPost && availablePosts.length === 0) {
      return <Navigate to={withServiceInstanceId('/profile', resolvedServiceInstanceId)} replace />
    }

    const preferredTarget = buildServiceVariantHomePath(currentPost, resolvedServiceInstanceId)
    const preferredPathname = preferredTarget.split('?')[0] || preferredTarget
    const safeDashboardTarget = evaluateRouteAccess(preferredPathname, currentPost).authorized
      ? preferredTarget
      : withServiceInstanceId('/dashboard', resolvedServiceInstanceId)

    if (typeof from === 'string') {
      const [fromPathname] = from.split('?')
      if (evaluateRouteAccess(fromPathname || from, currentPost).authorized) {
        return <Navigate to={from} replace />
      }
    }

    return <Navigate to={safeDashboardTarget} replace />
  }
  
  return <>{children}</>
}

function AuthTenantRoute({ children }: { children: React.ReactNode }) {
  return (
    <PublicRoute>
      <AuthThemeProvider>{children}</AuthThemeProvider>
    </PublicRoute>
  )
}

function LegacyInvoicePreviewRedirect() {
  const location = useLocation()
  const { jobId } = useParams<{ jobId: string }>()

  if (!jobId) {
    return <Navigate to={`/finance/invoices${location.search}${location.hash}`} replace />
  }

  return <Navigate to={`/finance/invoices/${jobId}${location.search}${location.hash}`} replace />
}

function LegacyServiceInstanceFormRedirect() {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  if (!id) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
  }

  return <Navigate to={`/org/service-instance/form/${id}${location.search}${location.hash}`} replace />
}

/** Legacy `.../config-form/:id` → unified organisation form (Configuration tab). */
function LegacyServiceInstanceConfigFormRedirect() {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)

  if (!id) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
  }

  const params = new URLSearchParams(location.search)
  if (!params.get('tab')) {
    params.set('tab', 'configuration')
  }
  const nextSearch = params.toString()
  return <Navigate to={`/org/service-instance/form/${id}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`} replace />
}

function LegacyServiceInstranceFormTypoRedirect() {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  if (!id) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
  }
  return <Navigate to={`/org/service-instance/form/${id}${location.search}${location.hash}`} replace />
}

function LegacyCommerceOrderDetailRedirect() {
  const location = useLocation()
  const { referenceId } = useParams<{ referenceId: string }>()
  return <Navigate to={`/services/orders/${referenceId ?? ''}${location.search}${location.hash}`} replace />
}

function LegacyJobReportsRedirect({
  targetPath,
}: {
  targetPath: '/job/reports' | '/job/reports/worker' | '/job/reports/company' | '/job/reports/client'
}) {
  const location = useLocation()
  return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />
}

function LegacyTimelineRedirect() {
  const location = useLocation()
  return <Navigate to={`/job/timeline${location.search}${location.hash}`} replace />
}

function LegacyEventFamilyAssignmentsRedirect({
  targetBasePath,
}: {
  targetBasePath: '/committees/assignments' | '/tasks/assignments'
}) {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  const suffix = (() => {
    if (location.pathname.endsWith('/create')) {
      return '/create'
    }

    if (id && location.pathname.endsWith('/details')) {
      return `/${id}/details`
    }

    if (id && location.pathname.endsWith('/edit')) {
      return `/${id}/edit`
    }

    return ''
  })()

  return <Navigate to={`${targetBasePath}${suffix}${location.search}${location.hash}`} replace />
}

function LegacyTagsetTableRedirect() {
  const location = useLocation()
  return <Navigate to={`/services/tagsets${location.search}${location.hash}`} replace />
}

function LegacyTagsRedirect() {
  const location = useLocation()
  const { productId } = useParams<{ productId: string }>()
  return <Navigate to={`/services/tagsets/${productId ?? ''}/tags${location.search}${location.hash}`} replace />
}

function LegacyItemTagsRedirect({ targetType }: { targetType: 'product' | 'product_variant' | 'category' | 'offer' | 'store' }) {
  const location = useLocation()
  const params = useParams<Record<string, string | undefined>>()
  const targetId = params.productId ?? params.categoryId ?? params.offerListRowId ?? params.storeId ?? ''
  const path = targetType === 'product'
    ? `/services/products/${targetId}/tags`
    : targetType === 'product_variant'
      ? `/services/product-variants/${targetId}/tags`
      : targetType === 'category'
        ? `/services/categories/${targetId}/tags`
        : targetType === 'offer'
          ? `/services/offers/${targetId}/tags`
          : `/services/stores/${targetId}/tags`
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />
}

function LegacyCommerceOrdersTabRedirect({
  tab,
}: {
  tab: 'types' | 'statuses' | 'flow'
}) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('tab', tab)
  const nextSearch = params.toString()
  return <Navigate to={`/services/orders${nextSearch ? `?${nextSearch}` : ''}${location.hash}`} replace />
}

function LegacyOrgUserGroupDirectoryRedirect({
  groupLabel,
}: {
  groupLabel: string
}) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  params.set('user_group_label', groupLabel)
  const nextSearch = params.toString()
  return <Navigate to={`/org/users${nextSearch ? `?${nextSearch}` : ''}${location.hash}`} replace />
}

function LegacyOrgUserGroupDetailRedirect({
  groupLabel,
}: {
  groupLabel: string
}) {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  if (!id) {
    return <LegacyOrgUserGroupDirectoryRedirect groupLabel={groupLabel} />
  }

  const params = new URLSearchParams(location.search)
  params.set('user_group_label', groupLabel)
  const nextSearch = params.toString()
  return <Navigate to={`/org/users/${id}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`} replace />
}

function LegacyServiceInstranceListTypoRedirect() {
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  if (!id) {
    return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
  }
  return <Navigate to={`/org/service-instance/${id}${location.search}${location.hash}`} replace />
}

function LegacyOrgBranchesRedirect() {
  const location = useLocation()
  return <Navigate to={`/org/service-instance${location.search}${location.hash}`} replace />
}

function OrgNodeLegacyRedirect() {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const target = isBranchOperatorWorkspaceContextFromPost(currentPost) ? '/org/branch' : '/org/organization'
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />
}

function OrganizationRootRedirect() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
}

/** Legacy `/org/tree` and `/organization/tree` → canonical organization workspace. */
function LegacyOrgTreeRedirect() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  return <Navigate to={withServiceInstanceId(ORG_ORGANIZATION_PAGE_PATH, serviceInstanceId)} replace />
}

export default function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <AuthTenantRoute>
            <LoginPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <AuthTenantRoute>
            <SignupPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/signup/company"
        element={
          <PublicRoute>
            <SignupCompanyPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup/services-company"
        element={
          <PublicRoute>
            <SignupServicesCompanyPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup/start/:vertical"
        element={
          <PublicRoute>
            <SignupStartCompanyPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup/vendor"
        element={
          <AuthTenantRoute>
            <SignupVendorLookupPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/signup/vendor/:orgInstanceId"
        element={
          <AuthTenantRoute>
            <SignupVendorPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/signup/:serviceInstanceId"
        element={
          <AuthTenantRoute>
            <SignupPublicPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <PublicRoute>
            <VerifyEmailPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AuthTenantRoute>
            <ForgotPasswordPage />
          </AuthTenantRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/job/:jobId/invoice/:invoiceId/payments" element={<PublicInvoicePaymentPage />} />
      <Route path="/invoice/pay/:paymentToken" element={<PublicInvoicePaymentPage />} />
      <Route path="/order/pay/:token" element={<PublicOrderPaymentPage />} />
      <Route path="/order/:orderId/payment/:paymentId" element={<PublicOrderPaymentPage />} />
      <Route path="/fundraising/campaign/:campaignId/donate" element={<PublicDonationPage />} />
      <Route path="/org/privacy-policy" element={<PublicPrivacyPolicyPage />} />
      <Route path="/org/account-deletion" element={<PublicAccountDeletionPage />} />
      <Route path="/auth/handoff" element={<AuthHandoffPage />} />
      <Route
        path="/billing/locked"
        element={
          <ProtectedRoute>
            <TrialExpiredPage />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <ProtectedLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="posts" element={<PostsSelectionPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="sales/dashboard" element={<SalesDashboardPage />} />
        <Route path="sales/enquiries" element={<SalesEnquiriesPage />} />
        <Route path="sales/enquiries/:enquiryId/follow-ups" element={<EnquiryFollowupsPage />} />
        <Route path="sales/follow-ups" element={<SalesFollowUpsPage />} />
        <Route path="job/dashboard" element={<DashboardPage />} />
        
        {/* Jobs */}
        <Route path="projects" element={<ProjectsListPage />} />
        <Route path="projects/create" element={<ProjectCreatePage />} />
        <Route path="projects/new" element={<ProjectCreatePage />} />
        <Route path="projects/lifecycle" element={<JobLifecyclePage />} />
        <Route path="projects/checklists" element={<JobChecklistsPage />} />
        <Route path="projects/configuration" element={<JobConfigurationPage />} />
        <Route path="projects/workflows" element={<ProjectWorkflowsPage />} />
        <Route path="projects/status-transitions" element={<ProjectWorkflowsPage />} />
        <Route path="project/locations" element={<LocationsPage />} />
        <Route path="projects/locations" element={<LocationsPage />} />
        <Route path="projects/customers" element={<JobCustomersPage />} />
        <Route path="projects/customers/:customerId" element={<CustomerDetailPage context="jobs" />} />
        <Route path="projects/assignments" element={<ProjectAssignmentsPage />} />
        <Route path="projects/workdays" element={<ProjectWorkdaysPage />} />
        <Route path="projects/:id" element={<JobDetailPage />} />
        <Route path="events" element={<EventsListPage />} />
        <Route path="events/dashboard" element={<EventsDashboardPage />} />
        <Route path="events/create" element={<EventCreatePage />} />
        <Route path="events/new" element={<EventCreatePage />} />
        <Route path="events/lifecycle" element={<EventLifecyclePage />} />
        <Route path="events/checklists" element={<EventChecklistsPage />} />
        <Route path="events/configuration" element={<EventConfigurationPage />} />
        <Route path="events/workflows" element={<EventWorkflowsPage />} />
        <Route path="events/status-transitions" element={<EventWorkflowsPage />} />
        <Route path="event/locations" element={<LocationsPage />} />
        <Route path="events/locations" element={<LocationsPage />} />
        <Route path="events/customers" element={<EventCustomersPage />} />
        <Route path="events/customers/:customerId" element={<CustomerDetailPage context="jobs" />} />
        <Route path="events/assignments" element={<EventAssignmentsPage />} />
        <Route path="events/assignments/create" element={<EventAssignmentCreatePage />} />
        <Route path="events/assignments/:id/details" element={<EventAssignmentDetailsPage />} />
        <Route path="events/assignments/:id/edit" element={<EventAssignmentEditPage />} />
        <Route path="committee/assignments" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/committees/assignments" />} />
        <Route path="committee/assignments/create" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/committees/assignments" />} />
        <Route path="committee/assignments/:id/details" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/committees/assignments" />} />
        <Route path="committee/assignments/:id/edit" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/committees/assignments" />} />
        <Route path="committees/assignments" element={<EventAssignmentsPage />} />
        <Route path="committees/assignments/create" element={<EventAssignmentCreatePage />} />
        <Route path="committees/assignments/:id/details" element={<EventAssignmentDetailsPage />} />
        <Route path="committees/assignments/:id/edit" element={<EventAssignmentEditPage />} />
        <Route path="task/assignments" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/tasks/assignments" />} />
        <Route path="task/assignments/create" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/tasks/assignments" />} />
        <Route path="task/assignments/:id/details" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/tasks/assignments" />} />
        <Route path="task/assignments/:id/edit" element={<LegacyEventFamilyAssignmentsRedirect targetBasePath="/tasks/assignments" />} />
        <Route path="tasks/assignments" element={<EventAssignmentsPage />} />
        <Route path="tasks/assignments/create" element={<EventAssignmentCreatePage />} />
        <Route path="tasks/assignments/:id/details" element={<EventAssignmentDetailsPage />} />
        <Route path="tasks/assignments/:id/edit" element={<EventAssignmentEditPage />} />
        <Route path="events/workdays" element={<EventDaysPage />} />
        <Route path="events/operations" element={<EventOperationsPage />} />
        <Route path="events/:id" element={<EventDetailPage />} />
        <Route path="job" element={<JobsListPage />} />
        <Route path="job/customers" element={<JobCustomersPage />} />
        <Route path="job/customers/:customerId" element={<CustomerDetailPage context="jobs" />} />
        <Route path="job/create" element={<JobCreatePage />} />
        <Route path="job/new" element={<JobCreatePage />} />
        <Route path="job/recurring" element={<JobsListPage />} />
        <Route path="job/recurring/create" element={<JobCreatePage />} />
        <Route path="job/live-jobs" element={<LiveJobsPage />} />
        <Route path="job/configuration" element={<JobConfigurationPage />} />
        <Route path="job/checklists" element={<JobChecklistsPage />} />
        <Route path="job/lifecycle" element={<JobLifecyclePage />} />
        <Route path="job/locations" element={<LocationsPage />} />
        <Route path="job/attendance" element={<AttendanceBoardPage />} />
        <Route path="job/attendance/live" element={<LiveAttendancePage />} />
        <Route path="job/attendance/logs" element={<TimeLogsPage />} />
        <Route path="job/attendance/clock" element={<ClockStationPage />} />
        <Route path="job/attendance/approvals" element={<AttendanceApprovalsPage />} />
        <Route path="live" element={<LivePage />} />
        <Route path="job/live" element={<LivePage />} />
        <Route path="job/realtime" element={<RealtimeOperationsPage />} />
        <Route path="jobs/scheduling" element={<ScheduleWorkersPage />} />
        <Route path="job/scheduling" element={<ScheduleWorkersPage />} />
        <Route path="job/schedule-workers" element={<ScheduleWorkersPage />} />
        <Route path="job/workerassignment" element={<LocationsPage />} />
        <Route path="job/recurrencejobworkerassignment" element={<LocationsPage />} />
        <Route path="job/workflows" element={<JobWorkflowsPage />} />
        <Route path="job/status-transitions" element={<JobWorkflowsPage />} />
        <Route path="job/complaints" element={<JobComplaintsPage />} />
        <Route path="job/:id" element={<JobDetailPage />} />
        <Route path="jobs" element={<Navigate to="/job" replace />} />
        <Route path="jobs/create" element={<Navigate to="/job/create" replace />} />
        <Route path="jobs/new" element={<Navigate to="/job/new" replace />} />
        <Route path="jobs/recurring" element={<Navigate to="/job/recurring" replace />} />
        <Route path="jobs/recurring/create" element={<Navigate to="/job/recurring/create" replace />} />
        <Route path="jobs/configuration" element={<Navigate to="/job/configuration" replace />} />
        <Route path="jobs/:id" element={<JobDetailPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="tags" element={<TagsPage />} />
        
        {/* Workers */}
        <Route path="workers" element={<WorkersListPage />} />
        <Route path="workers/create" element={<WorkerCreatePage />} />
        <Route path="workers/new" element={<WorkerCreatePage />} />
        <Route path="workers/approvals" element={<WorkerApprovalsPage />} />
        <Route path="workers/hotline-history" element={<WorkerHotlineHistoryPage />} />
        <Route path="org/suppliers" element={<LegacyOrgUserGroupDirectoryRedirect groupLabel="supplier" />} />
        <Route path="org/suppliers/:id" element={<LegacyOrgUserGroupDetailRedirect groupLabel="supplier" />} />
        <Route path="org/clients" element={<LegacyOrgUserGroupDirectoryRedirect groupLabel="vendor" />} />
        <Route path="org/clients/:id" element={<LegacyOrgUserGroupDetailRedirect groupLabel="vendor" />} />
        <Route path="org/users" element={<UsersPage />} />
        <Route path="org/user/clients" element={<UsersPage />} />
        <Route path="org/users/:id" element={<UserDetailPage />} />
        <Route path="suppliers" element={<LegacyOrgUserGroupDirectoryRedirect groupLabel="supplier" />} />
        <Route path="suppliers/:id" element={<LegacyOrgUserGroupDetailRedirect groupLabel="supplier" />} />
        <Route path="clients" element={<LegacyOrgUserGroupDirectoryRedirect groupLabel="vendor" />} />
        <Route path="clients/:id" element={<LegacyOrgUserGroupDetailRedirect groupLabel="vendor" />} />
        <Route path="users" element={<Navigate to="/org/users" replace />} />
        <Route path="user/clients" element={<Navigate to="/org/user/clients" replace />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="job/locations" element={<LocationsPage />} />
        <Route path="job/attendance" element={<AttendanceBoardPage />} />
        <Route path="job/attendance/live" element={<LiveAttendancePage />} />
        <Route path="job/attendance/logs" element={<TimeLogsPage />} />
        <Route path="job/attendance/clock" element={<ClockStationPage />} />
        <Route path="job/attendance/approvals" element={<AttendanceApprovalsPage />} />
        <Route path="live" element={<LivePage />} />
        <Route path="job/live" element={<LivePage />} />
        <Route path="job/live-jobs" element={<LiveJobsPage />} />
        <Route path="job/realtime" element={<RealtimeOperationsPage />} />
        <Route path="jobs/scheduling" element={<ScheduleWorkersPage />} />
        <Route path="job/scheduling" element={<ScheduleWorkersPage />} />
        <Route path="job/schedule-workers" element={<ScheduleWorkersPage />} />
        <Route path="job/workerassignment" element={<LocationsPage />} />
        <Route path="job/recurrencejobworkerassignment" element={<LocationsPage />} />
        <Route path="locations" element={<Navigate to="/job/locations" replace />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="assets/maintenance" element={<AssetMaintenanceBoardPage />} />
        <Route path="assets/configuration" element={<AssetConfigurationPage />} />
        <Route path="assets/reports" element={<AssetReportsPage />} />
        <Route path="assets/contracts" element={<AssetContractsPage />} />
        <Route path="assets/lookup" element={<AssetLookupPage />} />
        <Route path="assets/my" element={<MyAssetsPage />} />
        <Route path="assets/locations" element={<AssetLocationsPage />} />
        <Route path="assets/:id" element={<AssetDetailPage />} />
        <Route path="org/organization" element={<TreePage />} />
        <Route path="org/tree" element={<LegacyOrgTreeRedirect />} />
        <Route path="org/branch" element={<BranchWorkspacePage />} />
        <Route path="org/templates" element={<OrgTemplatesPage />} />
        <Route path="org/settings/credentials/meta" element={<MetaCredentialsPage />} />
        <Route path="org" element={<OrganizationRootRedirect />} />
        <Route path="org/node/:id" element={<OrgNodeLegacyRedirect />} />
        <Route path="org/node/child/:id" element={<TreeNodeChildPage />} />
        <Route path="org/node-details/:id" element={<TreeNodeDetailsPage />} />
        <Route path="org/service-instance/form/:id" element={<TreeServiceInstanceFormPage />} />
        <Route path="org/service-instance" element={<OrgServiceInstanceListRootRedirect />} />
        <Route path="org/service-instance/:id" element={<TreeServiceInstancesPage />} />
        <Route path="org/node/service-instrance/:id" element={<LegacyServiceInstranceListTypoRedirect />} />
        <Route path="org/node/branch-instrance/:id" element={<LegacyServiceInstranceListTypoRedirect />} />
        <Route path="org/service-instrance/form/:id" element={<LegacyServiceInstranceFormTypoRedirect />} />
        <Route path="org/node/service-instrance/form/:id" element={<LegacyServiceInstanceFormRedirect />} />
        <Route path="org/node/client-instrance/form/:id" element={<TreeServiceInstanceFormPage />} />
        <Route path="org/node/service-instrance/config-form/:id" element={<LegacyServiceInstanceConfigFormRedirect />} />
        <Route path="org/node/service-instrance/media-form/:id" element={<TreeServiceInstanceMediaPage />} />
        <Route path="org/node/branch-instrance/media-form/:id" element={<TreeServiceInstanceMediaPage />} />
        <Route path="org/node/client-add/:id" element={<TreeClientStoresPage />} />
        <Route path="org/node/subclient-add/:id" element={<TreeClientStoresPage />} />
        <Route path="org/client/child/:id" element={<TreeClientChildPage />} />
        <Route path="org/Client-branch-details/:id" element={<TreeClientDetailsPage />} />
        <Route path="org/client-details/:id" element={<TreeClientDetailsPage />} />
        <Route path="org/branches" element={<LegacyOrgBranchesRedirect />} />
        <Route path="org/tenants" element={<TenantsPage />} />
        <Route path="org/verticals" element={<OrganisationVerticalsPage />} />
        <Route path="org/in-apps" element={<InAppsPage />} />
        <Route path="tree" element={<Navigate to="/org/organization" replace />} />
        <Route path="organization" element={<Navigate to="/org" replace />} />
        <Route path="organization/organization" element={<TreePage />} />
        <Route path="organization/tree" element={<LegacyOrgTreeRedirect />} />
        <Route path="organization/branch" element={<BranchWorkspacePage />} />
        <Route path="organization/templates" element={<OrgTemplatesPage />} />
        <Route path="organization/settings/credentials/meta" element={<MetaCredentialsPage />} />
        <Route path="organization/node/:id" element={<OrgNodeLegacyRedirect />} />
        <Route path="organization/node/child/:id" element={<TreeNodeChildPage />} />
        <Route path="organization/node-details/:id" element={<TreeNodeDetailsPage />} />
        <Route path="organization/service-instance/form/:id" element={<TreeServiceInstanceFormPage />} />
        <Route path="organization/service-instance" element={<OrgServiceInstanceListRootRedirect />} />
        <Route path="organization/service-instance/:id" element={<TreeServiceInstancesPage />} />
        <Route path="organization/node/service-instrance/:id" element={<LegacyServiceInstranceListTypoRedirect />} />
        <Route path="organization/node/branch-instrance/:id" element={<LegacyServiceInstranceListTypoRedirect />} />
        <Route path="organization/service-instrance/form/:id" element={<LegacyServiceInstranceFormTypoRedirect />} />
        <Route path="organization/node/service-instrance/form/:id" element={<LegacyServiceInstanceFormRedirect />} />
        <Route path="organization/node/client-instrance/form/:id" element={<TreeServiceInstanceFormPage />} />
        <Route path="organization/node/service-instrance/config-form/:id" element={<LegacyServiceInstanceConfigFormRedirect />} />
        <Route path="organization/node/service-instrance/media-form/:id" element={<TreeServiceInstanceMediaPage />} />
        <Route path="organization/node/branch-instrance/media-form/:id" element={<TreeServiceInstanceMediaPage />} />
        <Route path="organization/node/client-add/:id" element={<TreeClientStoresPage />} />
        <Route path="organization/node/subclient-add/:id" element={<TreeClientStoresPage />} />
        <Route path="organization/client/child/:id" element={<TreeClientChildPage />} />
        <Route path="organization/Client-branch-details/:id" element={<TreeClientDetailsPage />} />
        <Route path="organization/client-details/:id" element={<TreeClientDetailsPage />} />
        <Route path="organisation/branches" element={<LegacyOrgBranchesRedirect />} />
        <Route path="organisation/tenants" element={<Navigate to="/org/tenants" replace />} />
        <Route path="organisation/verticals" element={<Navigate to="/org/verticals" replace />} />
        <Route path="workers/:id/edit" element={<WorkerEditPage />} />
        <Route path="workers/:id" element={<WorkerDetailPage />} />
        <Route path="job/workers" element={<WorkersListPage />} />
        <Route path="event/crew" element={<WorkersListPage />} />
        <Route path="events/crew" element={<WorkersListPage />} />

        {/* Roles */}
        <Route path="org/roles" element={<RolesPage />} />
        <Route path="help-support" element={<HelpSupportPage />} />
        <Route path="roles" element={<Navigate to="/org/roles" replace />} />
        <Route path="features" element={<Navigate to="/org/features" replace />} />
        <Route path="org/features" element={<FeaturesPage />} />
        <Route path="org/catalog-roles" element={<CatalogRolesPage />} />
        <Route path="manage-roles" element={<Navigate to="/org/manage-roles" replace />} />
        <Route path="org/manage-roles" element={<ManageRolesPage />} />
        
        {/* Assignments */}
        <Route path="job/assignment/list" element={<AssignmentsPage />} />
        <Route path="job/assignments" element={<AssignmentsPage />} />
        <Route path="jobs/workers/skill/:skillId" element={<JobSkillWorkersPage />} />
        <Route path="assignments/create" element={<AssignmentFormPage />} />
        <Route path="assignments/:id/details" element={<AssignmentJobViewPage />} />
        <Route path="assignments/:id/edit" element={<AssignmentFormPage />} />
        <Route path="assignments/:id" element={<AssignmentDetailPage />} />
        <Route path="assignments" element={<AssignmentsPage />} />
        
        {/* WorkDays */}
        <Route path="workdays" element={<WorkDaysPage />} />
        <Route path="workdays/create" element={<WorkDayEntryFormPage />} />
        <Route path="workdays/:id/edit" element={<WorkDayEntryFormPage />} />
        <Route path="job/timeline" element={<TimelinePage />} />
        <Route path="timeline" element={<LegacyTimelineRedirect />} />
        
        {/* Finance */}
        <Route path="job/expenses" element={<ExpensesPage />} />
        <Route path="finance/expenses" element={<ExpensesPage />} />
        <Route path="job/particulars" element={<ParticularsPage />} />
        <Route path="finance/particulars" element={<ParticularsPage />} />
        <Route path="finance/invoices" element={<FinanceInvoicesPage />} />
        <Route path="finance/invoices/:jobId/a4-preview" element={<JobInvoiceA4PreviewPage />} />
        <Route path="finance/invoices/:jobId" element={<JobInvoicesPage />} />
        <Route path="finance/invoices/:invoiceId/payments" element={<InvoicePaymentsPage />} />
        <Route path="finance/invoice-payments" element={<InvoicePaymentsListPage />} />
        <Route path="finance/invoice-preview" element={<LegacyInvoicePreviewRedirect />} />
        <Route path="finance/invoice-preview/:jobId" element={<LegacyInvoicePreviewRedirect />} />
        <Route path="job/worker/invoices" element={<WorkerInvoicesPage />} />
        <Route path="finance/worker-invoices" element={<WorkerInvoicesPage />} />
        <Route path="finance/worker-daily-invoices" element={<WorkerDailyInvoicesPage />} />
        <Route path="job/worker/draft-invoice" element={<WorkerDraftInvoicePage />} />
        <Route path="job/worker/payments" element={<WorkerPaymentsPage />} />
        <Route path="finance/worker-payments" element={<WorkerPaymentsPage />} />
        <Route path="job/company/payments" element={<CompanyPaymentsPage />} />
        <Route path="finance/company-payments" element={<CompanyPaymentsPage />} />
        <Route path="finance/settlement-invoices" element={<SettlementInvoicesPage />} />
        <Route path="finance/taxes" element={<CommerceTaxesPage />} />
        <Route path="job/settlement/invoices" element={<SettlementInvoicesPage />} />

        {/* Services */}
        <Route path="services/dashboard" element={<ServiceVariantDashboard />} />
        <Route path="membership/dashboard" element={<MembershipVariantDashboard />} />
        <Route path="academic" element={<Navigate to="/academic/dashboard" replace />} />
        <Route path="academic/dashboard" element={<AcademicDashboardPage />} />
        <Route path="academic/branches" element={<AcademicSectionPage section="branches" />} />
        <Route path="academic/courses" element={<AcademicSectionPage section="courses" />} />
        <Route path="academic/classes" element={<AcademicSectionPage section="classes" />} />
        <Route path="academic/subjects" element={<AcademicSectionPage section="subjects" />} />
        <Route path="academic/fees" element={<AcademicSectionPage section="fees" />} />
        <Route path="academic/people" element={<AcademicSectionPage section="people" />} />
        <Route path="academic/tagsets" element={<AcademicSectionPage section="tagsets" />} />
        <Route path="academic/tags" element={<AcademicSectionPage section="tags" />} />
        <Route path="academic/age-bands" element={<AcademicSectionPage section="ageBands" />} />
        <Route path="services/pos" element={<CommercePosPage />} />
        <Route path="services/complete-order" element={<CompleteOrderPage />} />
        <Route path="services/products" element={<CommerceProductsPage />} />
        <Route path="services/products/:productId/workspace" element={<CommerceProductVariantsPage />} />
        <Route path="services/product-variants" element={<CommerceProductVariantsPage />} />
        <Route path="services/categories" element={<CommerceCategoriesPage />} />
        <Route path="services/taxes" element={<CommerceTaxesPage />} />
        <Route path="services/worker-skills" element={<WorkerSkillsPage />} />
        <Route path="services/crew-templates" element={<CrewTemplatesRedirect />} />
        <Route path="services/crews" element={<CrewsPage />} />
        <Route path="services/crews/staff" element={<CrewsStaffPage />} />
        <Route path="services/pay-scales" element={<PayScalesPage />} />
        <Route path="services/memberships" element={<MembershipsPage />} />
        <Route path="services/memberships/subscriptions/:subscriptionId/members" element={<MembershipSubscriptionMembersPage />} />
        <Route path="services/memberships/subscriptions/:subscriptionId/payments" element={<MembershipSubscriptionPaymentsPage />} />
        <Route path="services/quotations" element={<CommerceQuotationsPage />} />
        <Route path="services/quotations/new" element={<CommerceQuotationFormPage />} />
        <Route path="services/quotations/new/pos" element={<CommerceQuotationPosPage />} />
        <Route path="services/quotations/:id/edit" element={<CommerceQuotationFormPage />} />
        <Route path="services/quotations/:id" element={<CommerceQuotationDetailPage />} />
        <Route path="services/orders" element={<CommerceOrdersPage />} />
        <Route path="services/bookings-dashboard" element={<CommerceBookingsDashboardPage />} />
        <Route path="services/credits" element={<CommerceCustomerCreditPage />} />
        <Route path="services/credits/:accountId" element={<CommerceCustomerCreditDetailPage />} />
        <Route path="services/order-payments" element={<CommerceOrderPaymentsPage />} />
        <Route path="services/refunds" element={<CommerceRefundsPage />} />
        <Route path="services/reviews" element={<CommerceReviewsPage />} />
        <Route path="services/refund-policies" element={<CommerceRefundPoliciesPage />} />
        <Route path="services/whatsapp" element={<WhatsAppMessagesPage />} />
        <Route path="services/whatsapp/catalog" element={<WhatsAppCatalogPage />} />
        <Route path="services/customers" element={<CommerceCustomersPage />} />
        <Route path="services/customers/:customerId" element={<CustomerDetailPage context="bookings" />} />
        <Route path="services/tagsets" element={<CommerceTagsetsPage />} />
        <Route path="services/tagsets/:tagsetId/tags" element={<CommerceTagsPage />} />
        <Route path="services/products/:productId/tags" element={<CommerceItemTagsPage targetType="product" />} />
        <Route path="services/product-variants/:variantId/tags" element={<CommerceItemTagsPage targetType="product_variant" />} />
        <Route path="services/categories/:categoryId/tags" element={<CommerceItemTagsPage targetType="category" />} />
        <Route path="services/master-categories/:masterCategoryId/tags" element={<CommerceItemTagsPage targetType="master_category" />} />
        <Route path="services/offers/:offerId/tags" element={<CommerceItemTagsPage targetType="offer" />} />
        <Route path="services/stores/:storeId/tags" element={<CommerceItemTagsPage targetType="store" />} />
        <Route path="services/manage-types" element={<LegacyCommerceOrdersTabRedirect tab="types" />} />
        <Route path="services/manage-statuses" element={<LegacyCommerceOrdersTabRedirect tab="statuses" />} />
        <Route path="services/status-flow" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="services/status-flows" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="services/orders/manage-types" element={<LegacyCommerceOrdersTabRedirect tab="types" />} />
        <Route path="services/orders/manage-statuses" element={<LegacyCommerceOrdersTabRedirect tab="statuses" />} />
        <Route path="services/orders/status-flow" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="services/orders/status-flows" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="services/orders/:referenceId" element={<CommerceOrderDetailPage />} />
        <Route path="CheckoutPage/Services/V1/OrderConfirmation" element={<OrderConfirmationPage />} />
        <Route path="services/reports/orders" element={<ReportsPage />} />
        <Route path="services/offers" element={<CommerceOffersPage />} />
        <Route path="planner" element={<PlannerTabRedirect />} />
        <Route path="planner/:tab" element={<PlannerPage />} />
        <Route path="commerce/pos" element={<Navigate to="/services/pos" replace />} />
        <Route path="commerce/complete-order" element={<Navigate to="/services/complete-order" replace />} />
        <Route path="commerce/products" element={<Navigate to="/services/products" replace />} />
        <Route path="commerce/product-variants" element={<Navigate to="/services/product-variants" replace />} />
        <Route path="commerce/categories" element={<Navigate to="/services/categories" replace />} />
        <Route path="commerce/quotations" element={<Navigate to="/services/quotations" replace />} />
        <Route path="commerce/orders" element={<Navigate to="/services/orders" replace />} />
        <Route path="commerce/tagsets" element={<Navigate to="/services/tagsets" replace />} />
        <Route path="commerce/manage-types" element={<LegacyCommerceOrdersTabRedirect tab="types" />} />
        <Route path="commerce/manage-statuses" element={<LegacyCommerceOrdersTabRedirect tab="statuses" />} />
        <Route path="commerce/status-flow" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="commerce/status-flows" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="commerce/orders/manage-types" element={<LegacyCommerceOrdersTabRedirect tab="types" />} />
        <Route path="commerce/orders/manage-statuses" element={<LegacyCommerceOrdersTabRedirect tab="statuses" />} />
        <Route path="commerce/orders/status-flow" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="commerce/orders/status-flows" element={<LegacyCommerceOrdersTabRedirect tab="flow" />} />
        <Route path="commerce/orders/:referenceId" element={<LegacyCommerceOrderDetailRedirect />} />
        <Route path="commerce/offers" element={<Navigate to="/services/offers" replace />} />
        <Route path="TagsetTable" element={<LegacyTagsetTableRedirect />} />
        <Route path="TagsetForm/:id" element={<LegacyTagsetTableRedirect />} />
        <Route path="Tags/:productId" element={<LegacyTagsRedirect />} />
        <Route path="Tags/Form/:id/:productId" element={<LegacyTagsRedirect />} />
        <Route path="Product/:productId/TagTable" element={<LegacyItemTagsRedirect targetType="product" />} />
        <Route path="Product/:id/:productId/TagForm" element={<LegacyItemTagsRedirect targetType="product" />} />
        <Route path="Product-varient/:id/:productId/TagForm" element={<LegacyItemTagsRedirect targetType="product_variant" />} />
        <Route path="Offer/:offerListRowId/TagTable" element={<LegacyItemTagsRedirect targetType="offer" />} />
        <Route path="Offer/:id/:offerListRowId/TagForm" element={<LegacyItemTagsRedirect targetType="offer" />} />
        <Route path="categories/:categoryId/TagTable" element={<LegacyItemTagsRedirect targetType="category" />} />
        <Route path="categories/:id/:categoryId/TagForm" element={<LegacyItemTagsRedirect targetType="category" />} />
        <Route path="store/:storeId/TagTable" element={<LegacyItemTagsRedirect targetType="store" />} />
        <Route path="store/:id/:storeId/TagForm" element={<LegacyItemTagsRedirect targetType="store" />} />

        {/* Reports */}
        <Route path="job/reports" element={<ReportsPage />} />
        <Route path="job/reports/worker" element={<WorkerReportPage />} />
        <Route path="job/reports/company" element={<CompanyReportPage />} />
        <Route path="job/reports/client" element={<ClientReportPage />} />
        <Route path="reports" element={<LegacyJobReportsRedirect targetPath="/job/reports" />} />
        <Route path="reports/orders" element={<LegacyJobReportsRedirect targetPath="/job/reports" />} />
        <Route path="reports/monthwise" element={<LegacyJobReportsRedirect targetPath="/job/reports" />} />
        <Route path="reports/worker" element={<LegacyJobReportsRedirect targetPath="/job/reports/worker" />} />
        <Route path="reports/company" element={<LegacyJobReportsRedirect targetPath="/job/reports/company" />} />
        <Route path="reports/client" element={<LegacyJobReportsRedirect targetPath="/job/reports/client" />} />
        
        {/* Marketing */}
        <Route path="marketing/announcements" element={<MarketingAnnouncementsPage />} />
        <Route path="marketing/announcements/create" element={<MarketingAnnouncementFormPage />} />
        <Route path="marketing/announcements/:id/edit" element={<MarketingAnnouncementFormPage />} />

        {/* Fundraising */}
        <Route path="fundraising/dashboard" element={<FundraisingDashboardPage />} />
        <Route path="fundraising" element={<FundraisingCampaignsPage />} />
        <Route path="fundraising/create" element={<FundraisingCampaignFormPage />} />
        <Route path="fundraising/categories" element={<FundraisingCategoriesPage />} />
        <Route path="fundraising/donations" element={<FundraisingDonationsPage />} />
        <Route path="fundraising/donors" element={<FundraisingDonorsPage />} />
        <Route path="fundraising/settings" element={<FundraisingSettingsPage />} />
        <Route path="fundraising/:id" element={<FundraisingCampaignDetailPage />} />
        <Route path="fundraising/:id/edit" element={<FundraisingCampaignFormPage />} />

          {/* Venue portal pages (Temple / Church / Charity) */}
          <Route path="venue/dashboard" element={<VenueDashboardPage />} />
          <Route path="venue/figures" element={<VenueFiguresPage />} />
          <Route path="venue/deities" element={<VenueDeitiesPage />} />
          <Route path="venue/festivals" element={<VenueFestivalsPage />} />
          <Route path="venue/events" element={<VenueEventsPage />} />
          <Route path="venue/volunteers" element={<VenueVolunteersPage />} />
          <Route path="venue/calendar" element={<VenueCalendarPage />} />
          <Route path="venue/news" element={<VenueNewsPage />} />
          <Route path="venue/admin/home" element={<VenueHomeContentAdminPage />} />
          <Route path="venue/admin/home/about" element={<VenueAboutTempleAdminPage />} />
          <Route path="venue/admin/home/activity" element={<VenueActivityAdminPage />} />
          <Route path="venue/admin/home/vision-mission" element={<VenueVisionMissionAdminPage />} />
          <Route path="venue/admin/home/sections/:sectionId/gallery" element={<VenueGalleryAdminPage />} />
          <Route path="venue/admin/home/sections/:sectionId/vision-mission" element={<VenueVisionMissionAdminPage />} />
          <Route path="venue/admin/home/sections/:sectionId/records" element={<VenueDynamicSectionRecordsAdminPage />} />
          <Route path="venue/admin/home/section-labels/:sectionKey" element={<VenueSectionLabelsAdminPage />} />
          <Route path="venue/admin/pooja-timings" element={<VenuePoojaTimingsAdminPage />} />
          <Route path="venue/admin/languages" element={<VenueLanguagesAdminPage />} />
          <Route path="venue/top-banners/edit" element={<VenueTopBannersEditPage />} />
        
        {/* Profile */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings/features" element={<FeaturesSettingsPage />} />
      </Route>

      {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
